import {
  ArrowBack,
  Cancel,
  CheckCircle,
  Lock,
  Visibility,
  VisibilityOff,
  VpnKey,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { logError } from '@ptek/client-logger';
import React, { useEffect, useMemo, useState } from 'react';
import { FlexColumn, FlexRow, IconCircle } from '../../../components/LayoutBoxes';
import { useWidgetAuth } from '../../../context/AuthContext';
import { iconStyles } from '../../../styles/iconStyles';
import { authStyles as styles } from '../styles/authStyles';

const CHALLENGE_TIMEOUT_MS = 3 * 60 * 1000;

const PASSWORD_REQUIREMENTS = [
  {
    id: 'length',
    text: 'At least 8 characters long',
    test: (password: string) => password.length >= 8,
  },
];

const validatePassword = (password: string): string | null => {
  const unmetRequirements = PASSWORD_REQUIREMENTS.filter(
    (req) => !req.test(password)
  );
  if (unmetRequirements.length > 0) {
    return `Password requirements not met: ${unmetRequirements.map((req) => req.text.toLowerCase()).join(', ')}`;
  }
  return null;
};

interface NewPasswordFormProps {
  username: string;
}

export const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
  username,
}) => {
  const { completeNewPassword, clearChallenge, isLoading, error } =
    useWidgetAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [challengeExpired, setChallengeExpired] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setChallengeExpired(true);
    }, CHALLENGE_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, []);

  const passwordRequirements = PASSWORD_REQUIREMENTS;

  const requirementStatus = useMemo(() => {
    return passwordRequirements.map((req) => ({
      ...req,
      met: req.test(newPassword),
    }));
  }, [newPassword, passwordRequirements]);

  const allRequirementsMet = useMemo(() => {
    return requirementStatus.every((req) => req.met);
  }, [requirementStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setValidationError(null);

    const passwordValidation = validatePassword(newPassword);
    if (passwordValidation) {
      setValidationError(passwordValidation);
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    try {
      await completeNewPassword(username, newPassword);
    } catch (err) {
      logError('NewPasswordForm', 'New password completion failed:', err);

      const errorMessage =
        err instanceof Error ? err.message.toLowerCase() : '';
      if (
        errorMessage.includes('expired') ||
        errorMessage.includes('invalid session') ||
        errorMessage.includes('session')
      ) {
        setChallengeExpired(true);
      }
    }
  };

  const handleBackToLogin = () => {
    clearChallenge();
  };

  const passwordsMatch = newPassword === confirmPassword;
  const hasConfirmPassword = confirmPassword.length > 0;

  return (
    <FlexColumn sx={styles.newPasswordCard}>
      {/* Header */}
      <Box sx={styles.newPasswordHeader}>
        <Box sx={styles.newPasswordBackButtonContainer}>
          <IconButton
            onClick={handleBackToLogin}
            size='small'
            disabled={isLoading}
            sx={styles.newPasswordBackButton}
          >
            <ArrowBack sx={iconStyles.ml} />
          </IconButton>
        </Box>

        <IconCircle sx={styles.newPasswordIconCircleWarning}>
          <VpnKey sx={iconStyles.circleIcon} />
        </IconCircle>
        <Typography variant='h6' sx={styles.newPasswordTitle}>
          Set New Password
        </Typography>
        <Typography variant='body2' sx={styles.newPasswordSubtitle}>
          {username}
        </Typography>
      </Box>

      {/* Form */}
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={styles.newPasswordForm}
      >
        <TextField
          label='New Password'
          type={showNewPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          disabled={isLoading}
          variant='outlined'
          size='medium'
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Lock sx={styles.inputIcon} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge='end'
                  disabled={isLoading}
                  size='small'
                  sx={styles.visibilityToggle}
                >
                  {showNewPassword ? (
                    <VisibilityOff sx={iconStyles.lg} />
                  ) : (
                    <Visibility sx={iconStyles.lg} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          color="warning"
        />

        <TextField
          label='Confirm Password'
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={isLoading}
          variant='outlined'
          size='medium'
          fullWidth
          error={hasConfirmPassword && !passwordsMatch}
          helperText={
            hasConfirmPassword && !passwordsMatch
              ? 'Passwords do not match'
              : hasConfirmPassword && passwordsMatch
                ? 'Passwords match ✓'
                : ''
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Lock sx={styles.inputIcon} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge='end'
                  disabled={isLoading}
                  size='small'
                  sx={styles.visibilityToggle}
                >
                  {showConfirmPassword ? (
                    <VisibilityOff sx={iconStyles.lg} />
                  ) : (
                    <Visibility sx={iconStyles.lg} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          color={hasConfirmPassword && !passwordsMatch ? 'error' : 'warning'}
        />

        {/* Requirements */}
        <Box sx={styles.newPasswordRequirementsBox}>
          <Typography variant='subtitle2' sx={styles.newPasswordRequirementsTitle}>
            Requirements:
          </Typography>

          {requirementStatus.map((requirement) => (
            <FlexRow key={requirement.id} sx={styles.newPasswordRequirementRow}>
              {requirement.met ? (
                <CheckCircle sx={iconStyles.successCheck} />
              ) : (
                <Cancel sx={iconStyles.disabledIcon} />
              )}
              <Typography
                variant='body2'
                sx={{
                  fontSize: '12px',
                  color: requirement.met ? 'success.main' : 'text.secondary',
                  fontWeight: requirement.met ? 500 : 400,
                }}
              >
                {requirement.text}
              </Typography>
            </FlexRow>
          ))}
        </Box>

        {/* Alerts */}
        {challengeExpired && (
          <Alert
            severity='warning'
            icon={<Warning sx={iconStyles.md} />}
            variant='outlined'
            sx={styles.newPasswordAlertStyle}
          >
            This password reset session may have expired. If you encounter
            issues, please go back and try logging in again.
          </Alert>
        )}

        {(validationError || error) && (
          <Alert severity='error' variant='outlined' sx={styles.newPasswordAlertStyle}>
            {validationError || error?.message || 'Failed to set password'}
          </Alert>
        )}

        {/* Submit button */}
        <Button
          variant='contained'
          type='submit'
          disabled={
            isLoading ||
            !allRequirementsMet ||
            !confirmPassword ||
            !passwordsMatch
          }
          size='large'
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              <VpnKey sx={iconStyles.lg} />
            )
          }
          sx={{
            ...styles.newPasswordSubmitButton,
            backgroundColor: styles.getSubmitButtonBgColor(challengeExpired),
          }}
        >
          {isLoading
            ? 'Setting...'
            : challengeExpired
              ? 'Try Set Password'
              : 'Set Password'}
        </Button>
      </Box>
    </FlexColumn>
  );
};
